/**
 * Custom hook for managing reading notes
 */

import { useState, useCallback } from 'react';
import { api } from '../config/api';
import { generateSelectionGroupId } from '../utils/idGenerators';
import {
  ReadingNote,
  ReadingNoteCreate,
  MultiPageSelection,
  IndexingStatus,
  createNotesFromMultiPageSelection
} from '../types/reading-notes';

export function useReadingNotes(bookId: string) {
  const [notes, setNotes] = useState<ReadingNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch notes for a specific page
  const fetchNotesForPage = useCallback(async (pageNumber: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/books/${bookId}/notes`, {
        params: { page: pageNumber }
      });
      setNotes(response.data.notes);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch notes');
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Create a single note
  const createNote = useCallback(async (note: ReadingNoteCreate) => {
    try {
      setLoading(true);
      const response = await api.post(`/api/books/${bookId}/notes`, note);
      setNotes(prev => [...prev, response.data]);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create note');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Create multiple notes (for multi-page selections)
  const createMultiPageNotes = useCallback(async (
    selections: MultiPageSelection[],
    userNote: string,
    conceptTags: string[] = []
  ) => {
    try {
      setLoading(true);
      
      // Generate a unique group ID for this multi-page selection
      const selectionGroupId = generateSelectionGroupId();
      
      // Create note objects for each page
      const notes = createNotesFromMultiPageSelection(
        bookId,
        selections,
        userNote,
        conceptTags,
        selectionGroupId
      );
      
      // Send all notes to backend (could be batch endpoint)
      const promises = notes.map(note => 
        api.post(`/api/books/${bookId}/notes`, note)
      );
      
      const responses = await Promise.all(promises);
      const createdNotes = responses.map(r => r.data);
      
      setNotes(prev => [...prev, ...createdNotes]);
      return createdNotes;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create multi-page notes');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [bookId, createNote]);

  // Update a note
  const updateNote = useCallback(async (noteId: string, updates: Partial<ReadingNote>) => {
    try {
      setLoading(true);
      const response = await api.patch(`/api/books/${bookId}/notes/${noteId}`, updates);
      setNotes(prev => prev.map(n => n.id === noteId ? response.data : n));
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update note');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Delete a note
  const deleteNote = useCallback(async (noteId: string) => {
    try {
      setLoading(true);
      await api.delete(`/api/books/${bookId}/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete note');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Delete all notes in a selection group
  const deleteSelectionGroup = useCallback(async (selectionGroupId: string) => {
    try {
      setLoading(true);
      await api.delete(`/api/books/${bookId}/notes/group/${selectionGroupId}`);
      setNotes(prev => prev.filter(n =>
        n.note_metadata?.selection_group_id !== selectionGroupId
      ));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete selection group');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Get indexing status for a note
  const getIndexingStatus = useCallback(async (noteId: string) => {
    try {
      const response = await api.get(`/api/books/${bookId}/notes/${noteId}/indexing-status`);
      return response.data;
    } catch (err: any) {
      console.error('Failed to fetch indexing status:', err);
      return null;
    }
  }, [bookId]);

  // Update note's indexing status in local state
  const updateNoteIndexingStatus = useCallback((noteId: string, status: Partial<ReadingNote>) => {
    setNotes(prev => prev.map(n =>
      n.id === noteId ? { ...n, ...status } : n
    ));
  }, []);

  return {
    notes,
    loading,
    error,
    fetchNotesForPage,
    createNote,
    createMultiPageNotes,
    updateNote,
    deleteNote,
    deleteSelectionGroup,
    getIndexingStatus,
    updateNoteIndexingStatus
  };
}